/**
 * Match Notes - consolidated view of every match that has coach's notes,
 * with the match context (opponent, date, competition, venue, score) and
 * the list of notes rendered alongside.
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatDateUK } from '../lib/utils';
import { toast } from 'sonner';
import {
  NotebookPen, Search, Calendar, Trophy, MapPin, Eye, Lock, Tag,
  ArrowRight, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveBadgeUrl = (badge) => {
  if (!badge) return null;
  if (badge.startsWith('http://') || badge.startsWith('https://')) return badge;
  return `${BACKEND_URL}${badge}`;
};

const venueLabel = (v) => (v === 'home' ? 'Home' : v === 'away' ? 'Away' : 'Neutral');

const getMatchResult = (m) => {
  if (m.status !== 'result') return null;
  const ours = m.our_score_et ?? m.our_score ?? 0;
  const theirs = m.opponent_score_et ?? m.opponent_score ?? 0;
  if (ours > theirs) return { label: 'W', color: 'bg-emerald-100 text-emerald-700' };
  if (ours < theirs) return { label: 'L', color: 'bg-red-100 text-red-700' };
  return { label: 'D', color: 'bg-amber-100 text-amber-700' };
};

export default function MatchNotes() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [opponentBadgeMap, setOpponentBadgeMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const [matchesRes, oppRes] = await Promise.all([
          axios.get(`${API}/matches`),
          axios.get(`${API}/opponents`).catch(() => ({ data: [] }))
        ]);
        setMatches(matchesRes.data || []);
        const map = {};
        (oppRes.data || []).forEach(o => { if (o?.name) map[o.name.toLowerCase().trim()] = o.badge || null; });
        setOpponentBadgeMap(map);
      } catch {
        toast.error('Failed to load match notes');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const matchesWithNotes = useMemo(() => {
    return matches
      .filter(m => Array.isArray(m.coach_notes) && m.coach_notes.length > 0)
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [matches]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    matchesWithNotes.forEach(m => m.coach_notes.forEach(n => n.category && set.add(n.category)));
    const fromSettings = user?.coach_note_categories || [];
    fromSettings.forEach(c => set.add(c));
    return [...set];
  }, [matchesWithNotes, user?.coach_note_categories]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return matchesWithNotes
      .map(m => {
        const notes = (m.coach_notes || []).filter(n => {
          if (categoryFilter !== 'all') {
            if (categoryFilter === '__uncategorised__' && n.category) return false;
            if (categoryFilter !== '__uncategorised__' && n.category !== categoryFilter) return false;
          }
          if (visibilityFilter !== 'all' && (n.visibility || 'private') !== visibilityFilter) return false;
          if (q) {
            const hay = `${n.body} ${n.category || ''} ${m.opponent || ''} ${m.competition || ''}`.toLowerCase();
            if (!hay.includes(q)) return false;
          }
          return true;
        });
        return { ...m, coach_notes: notes };
      })
      .filter(m => m.coach_notes.length > 0);
  }, [matchesWithNotes, search, categoryFilter, visibilityFilter]);

  const totalNotes = useMemo(() => matchesWithNotes.reduce((n, m) => n + m.coach_notes.length, 0), [matchesWithNotes]);

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="match-notes-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">Match Notes</h1>
            <p className="text-sm text-gray-500 mt-1">
              {totalNotes} note{totalNotes === 1 ? '' : 's'} across {matchesWithNotes.length} match{matchesWithNotes.length === 1 ? '' : 'es'}.
            </p>
          </div>
        </div>

        {/* Filters */}
        {matchesWithNotes.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-2" data-testid="match-notes-controls">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notes, opponents, competitions…"
                className="pl-9 rounded-xl"
                data-testid="match-notes-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48 rounded-xl" data-testid="match-notes-category-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                <SelectItem value="__uncategorised__">Uncategorised</SelectItem>
              </SelectContent>
            </Select>
            <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
              <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="match-notes-visibility-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All visibility</SelectItem>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="public">Public</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading…</div>
        ) : matchesWithNotes.length === 0 ? (
          <EmptyState
            title="No match notes yet"
            body="Open a match and switch to the Notes tab to add your first coach's note — it'll appear here."
          />
        ) : filtered.length === 0 ? (
          <EmptyState title="No notes match your filters" body="Try clearing the search or changing the filters." />
        ) : (
          <div className="flex flex-col gap-6" data-testid="match-notes-list">
            {filtered.map(m => (
              <MatchNotesCard key={m.id} match={m} opponentBadgeMap={opponentBadgeMap} showOpponentBadges={user?.show_opponent_badges !== false} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function EmptyState({ title, body }) {
  return (
    <Card className="border-dashed">
      <CardContent className="text-center py-12">
        <NotebookPen className="w-12 h-12 mx-auto mb-3 text-gray-300" strokeWidth={1.5} />
        <p className="font-medium text-gray-700">{title}</p>
        <p className="text-sm text-gray-500 mt-1">{body}</p>
      </CardContent>
    </Card>
  );
}

function MatchNotesCard({ match, opponentBadgeMap, showOpponentBadges = true }) {
  const badge = resolveBadgeUrl(opponentBadgeMap[(match.opponent || '').toLowerCase().trim()]);
  const result = getMatchResult(match);
  const scoreText = match.status === 'result'
    ? `${match.our_score_et ?? match.our_score ?? 0} - ${match.opponent_score_et ?? match.opponent_score ?? 0}`
    : null;

  return (
    <Card data-testid={`match-notes-card-${match.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {showOpponentBadges && (
              badge ? (
                <img src={badge} alt={`${match.opponent} badge`} className="w-12 h-12 object-contain shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0">
                  <Shield className="w-6 h-6 text-gray-300" strokeWidth={1.5} />
                </div>
              )
            )}
            <div className="min-w-0">
              <CardTitle className="text-xl font-semibold font-title truncate">{match.opponent}</CardTitle>
              <div className="flex items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500 flex-wrap">
                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" strokeWidth={1.5} />{formatDateUK(match.date)}</span>
                {match.competition && <span className="inline-flex items-center gap-1"><Trophy className="w-3 h-3" strokeWidth={1.5} />{match.competition}</span>}
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.5} />{venueLabel(match.venue)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {scoreText && (
              <div className="text-right">
                <p className="text-lg font-bold text-gray-900 tracking-tight">{scoreText}</p>
                {result && (
                  <span
                    className={`inline-flex items-center justify-center h-[22px] font-bold ${result.color}`}
                    style={{ fontSize: '8px', minWidth: '18px', paddingLeft: '0.275rem', paddingRight: '0.275rem', borderRadius: 'calc(var(--radius) - 6px)' }}
                  >
                    {result.label}
                  </span>
                )}
              </div>
            )}
            <Link to={`/matches/${match.id}/edit?tab=notes`}>
              <Button variant="outline" size="sm" className="rounded-lg" data-testid={`match-notes-open-${match.id}`}>
                Open
                <ArrowRight className="w-3.5 h-3.5 ml-1" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-col gap-3">
          {match.coach_notes.map(note => {
            const isPublic = (note.visibility || 'private') === 'public';
            return (
              <article
                key={note.id}
                className="p-4 rounded-2xl bg-gray-50 border border-gray-200"
                data-testid={`match-notes-entry-${note.id}`}
              >
                <header className="flex flex-wrap items-center gap-2 text-xs">
                  {note.category && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                      <Tag className="w-3 h-3" strokeWidth={2} />{note.category}
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${isPublic ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'}`}>
                    {isPublic ? <Eye className="w-3 h-3" strokeWidth={2} /> : <Lock className="w-3 h-3" strokeWidth={2} />}
                    {isPublic ? 'Public' : 'Private'}
                  </span>
                  {note.created_at && (
                    <span className="text-gray-400">{formatDateUK(note.created_at.slice(0, 10))}</span>
                  )}
                </header>
                <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{note.body}</p>
              </article>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
